import { Block } from '@helvetia/components/Block'
import { Box } from '@helvetia/components/Box'
import { Button } from '@helvetia/components/Button'
import { Col, Row } from '@helvetia/components/layout'
import LinkText from '@helvetia/components/LinkText'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { breakpoints } from '@helvetia/components/theme/breakpoints'
import dayjs from 'dayjs'
import { Field, Form, Formik } from 'formik'
import numeral from 'numeral'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { PriceDTO } from '../../../core/domain/offerDocument.dto'
import AgentService from '../../../core/services/agent.service'
import { CheckboxField } from '../../_components/CheckboxField'
import { LifecycleView } from '../../_components/ConnectComponent'
import ContactBox from '../../_components/HelvetiaContactBox'
import {
  CalendarIcon,
  ClickableIcon,
  DeductibleIcon,
  LiabilityIcon,
  LocationScopeIcon,
  SumInsuredIcon,
  TypeGoodsIcon,
  VehiclesIcon,
} from '../../_components/icons/icons'
import PdfSVG from '../../_components/icons/pdf.svg'
import { Modal } from '../../_components/Modal'
import PriceBoxItem from '../../_components/summary/PriceBoxItem'
import { PageWrapper } from '../../_components/summary/Summary.styles'
import SummaryItem from '../../_components/summary/SummaryItem'
import { PolicyViewModel } from './Policy.model'
import { PolicyPresenter } from './Policy.presenter'
import { validateFieldForm } from './Policy.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

interface Props {
  presenter: PolicyPresenter
}

interface State {
  policy: PolicyViewModel
  informedTermsAndConditions: boolean
  obtainedPrice: PriceDTO
  isLoading: boolean
  isModalOpen: boolean
  isPolicyContracted: boolean
}

export class PolicyRaw
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: PolicyPresenter
  private transportingText: string = undefined

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.state = {
      policy: null,
      obtainedPrice: null,
      informedTermsAndConditions: false,
      isLoading: true,
      isModalOpen: false,
      isPolicyContracted: false,
    }
  }

  setInitialValues = (
    policy,
    informedTermsAndConditions,
    obtainedPrice,
    isPolicyContracted,
  ) => {
    this.setState({
      policy,
      informedTermsAndConditions,
      obtainedPrice,
      isPolicyContracted,
      isLoading: false,
    })
  }
  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  showModal = () => this.setState({ isModalOpen: true })

  hideModal = () => this.setState({ isModalOpen: false })

  handleContractClick = formValue => {
    this.presenter.onContract(formValue.informedTermsAndConditions)
  }

  checkTransportingMessage = () => {
    let prevRemoval: boolean = false
    this.transportingText = ''
    if (this.state.policy.transRemoval > 0) {
      this.transportingText = this.props.t('summary.transRemoval', {
        value: this.state.policy.transRemoval,
      })
      prevRemoval = true
    }

    if (this.state.policy.transTheft > 0) {
      this.transportingText += prevRemoval ? this.props.t('summary.and') : ''
      this.transportingText += this.props.t('summary.transTheft', {
        value: this.state.policy.transTheft,
      })
    }
  }

  render() {
    const { t } = this.props
    const {
      policy,
      obtainedPrice,
      informedTermsAndConditions,
      isPolicyContracted,
      isLoading,
      isModalOpen,
    } = this.state
    this.transportingText = undefined

    if (isLoading) return <Spinner size="xl" />

    let downloadButtonStyles = {
      display: 'flex',
      alignContent: 'baseline',
      justifyContent: 'center',
    }

    if (window.matchMedia(`(min-width: ${breakpoints.tablet}px)`).matches) {
      delete downloadButtonStyles.justifyContent
    }

    if (isModalOpen) {
      if (AgentService.isAgentOrBroker()) {
        return (
          <Modal
            buttonText={t('buttons.accept')}
            onClose={this.presenter.onHideModalHandler}
            title={
              <>
                <MatchMedia onlyMobileAndTablet>
                  <Text size="sBold">{t('modal.agentBroker.title')}</Text>
                </MatchMedia>
                <MatchMedia desktop>
                  <Text size="sBold">{t('modal.agentBroker.title')}</Text>
                </MatchMedia>
              </>
            }
          >
            <Text size="s" as="p" color="black">
              {t('modal.agentBroker.p2')}
            </Text>
            <Text size="s" as="p" color="black">
              {t('modal.agentBroker.p3')}
            </Text>
          </Modal>
        )
      } else {
        return (
          <Modal
            buttonText={t('buttons.accept')}
            onClose={this.presenter.onHideModalHandler}
            title={
              <>
                <MatchMedia onlyMobileAndTablet>
                  <Text size="sBold">{t('modal.contract.title')}</Text>
                </MatchMedia>
                <MatchMedia desktop>
                  <Text size="sBold">{t('modal.contract.title')}</Text>
                </MatchMedia>
              </>
            }
          >
            <Text size="s" as="p" color="black">
              {t('modal.contract.p1')}
            </Text>
            <Text size="s" as="p" color="black">
              {t('modal.contract.p2')}
            </Text>
            <Text size="s" as="p" color="black">
              {t('modal.contract.p3')}
            </Text>
            <Text size="s" as="p" color="black">
              {t('contactMe.questions')}
              <Text as="span" color="black" size="xsBold">
                {` marine@helvetia.ch`}
              </Text>
            </Text>
          </Modal>
        )
      }
    }

    this.checkTransportingMessage()

    return (
      <PageWrapper>
        <Row>
          <Text as="p" color="black" size="l">
            {t('summary.title')}
          </Text>
        </Row>

        <Row verticalGutter marginBottom="s" marginTop="xxs">
          <Col verticalGutter mobile={12} tablet={12} desktop={12}>
            <Text as="p" size="sBold" color="black" uppercase>
              {t('summary.subtitle.conditions')}
            </Text>
          </Col>
          <Col mobile={12} tablet={6} desktop={6} verticalGutter>
            {policy.vehicles > 0 && (
              <SummaryItem
                bottom="xxxs"
                icon={<VehiclesIcon />}
                editionDisabled
              >
                {t('summary.vehicles', {
                  value: `${policy.vehicles}`,
                })}
              </SummaryItem>
            )}
            {policy.globalInsured > 0 ? (
              <SummaryItem
                top="xxxs"
                bottom="xxxs"
                icon={<SumInsuredIcon />}
                editionDisabled
              >
                {t('summary.globalInsured', {
                  value: numeral(policy.globalInsured).format('$ 0,0.00'),
                })}
              </SummaryItem>
            ) : (
              <>
                {[...Array(5)].map((x, i) => (
                  <>
                    {policy[`insuredVehicle${i + 1}`] > 0 && (
                      <SummaryItem
                        key={i}
                        top="xxxs"
                        bottom="xxxs"
                        icon={<SumInsuredIcon />}
                        editionDisabled
                      >
                        {t('summary.vehiclesInsured', {
                          value: numeral(
                            policy[`insuredVehicle${i + 1}`],
                          ).format('$ 0,0.00'),
                          index: `${policy[`plate${i + 1}`]}`,
                        })}
                      </SummaryItem>
                    )}
                  </>
                ))}
              </>
            )}

            {policy.deductible > 0 && (
              <SummaryItem
                top="xxxs"
                bottom="xxxs"
                icon={<DeductibleIcon />}
                editionDisabled
              >
                {t('summary.deductible', {
                  value: numeral(policy.deductible).format('$ 0,0.00'),
                })}
              </SummaryItem>
            )}

            <SummaryItem
              top="xxxs"
              bottom="xxxs"
              icon={<LocationScopeIcon />}
              editionDisabled
            >
              {policy.transEurope > 0
                ? t('summary.transEurope')
                : t('summary.transCH')}
            </SummaryItem>

            {this.transportingText && (
              <SummaryItem icon={<TypeGoodsIcon />} editionDisabled>
                {this.transportingText}
              </SummaryItem>
            )}

            <SummaryItem
              top="xxxs"
              bottom="xxxs"
              icon={<LiabilityIcon />}
              editionDisabled
            >
              {t('summary.liability', {
                value: t(`step4.liability.${policy.liability}`),
              })}
            </SummaryItem>

            <SummaryItem
              top="xxxs"
              bottom="xxxs"
              icon={<CalendarIcon />}
              editionDisabled
            >
              {`${t('summary.calendar.startOn', {
                date: `${dayjs(policy.validityStartDate).format('DD.MM.YYYY')}`,
              })}. ${t('summary.calendar.duration', {
                years: `${policy.validityInYears}`,
              })}  `}
            </SummaryItem>
          </Col>

          <Col verticalGutter mobile={12} tablet={6} desktop={6}>
            <Box tertiary size="xxs">
              <PriceBoxItem
                leftText={t('summary.price')}
                rightText={numeral(obtainedPrice.price).format('$ 0,0.00')}
              />
              <PriceBoxItem
                leftText={t('summary.taxes')}
                rightText={numeral(obtainedPrice.taxes).format('$ 0,0.00')}
              />
              <PriceBoxItem
                bold
                leftText={'TOTAL'}
                rightText={numeral(
                  obtainedPrice.price + obtainedPrice.taxes,
                ).format('$ 0,0.00')}
              />
            </Box>
          </Col>
        </Row>
        <Row marginBottom="xs">
          <Col mobile={12} tablet={12} desktop={12}>
            <Text as="p" size="sBold" color="black" uppercase>
              {t('summary.subtitle.policyholder')}
            </Text>
          </Col>

          <Col verticalGutter mobile={12} tablet={12} desktop={12}>
            <Text as="div" size="s" color="black">
              <Text as="span" size="sBold" color="black">
                {t('summary.policyholder.company')}
              </Text>
              {policy.company}
            </Text>

            <Text as="div" size="s" color="black">
              <Text as="span" size="sBold" color="black">
                {t('summary.policyholder.contact')}
              </Text>
              {`${policy.firstName} ${policy.lastName}`}
            </Text>

            <Text as="div" size="s" color="black">
              <Text as="span" size="sBold" color="black">
                {t('summary.policyholder.email')}
              </Text>
              {policy.email}
            </Text>

            <Text as="div" size="s" color="black">
              <Text as="span" size="sBold" color="black">
                {t('summary.policyholder.address')}
              </Text>
              {`${policy.postalAddress} ${
                policy.postalAddressOpt ? policy.postalAddressOpt : ''
              }, ${policy.postalCode} ${policy.city}`}
            </Text>
          </Col>
        </Row>

        {AgentService.isAgentOrBroker() ? (
          <>
            <Row marginTop="xxs" marginBottom="xs">
              <Col verticalGutter mobile={12} tablet={12} desktop={12}>
                <Text
                  as="div"
                  size="sBold"
                  color="black"
                  marginBottom="xxs"
                  uppercase
                >
                  {t('summary.subtitle.generalTerms')}
                </Text>
              </Col>
              <Col verticalGutter mobile={12} tablet={12} desktop={12}>
                <Text as="div" size="s" color="black">
                  {t('summary.generalTerms.main')}
                  <span>
                    <LinkText
                      onClick={this.presenter.onShowGeneralConditionPDF}
                      text={t('summary.generalTerms.link')}
                      color="primary"
                    />
                  </span>
                </Text>
              </Col>
            </Row>
            <Block flex responsive>
              {!isPolicyContracted && (
                <Block
                  grow={1}
                  marginRightTablet="xs"
                  marginRightDesktop="xs"
                  marginLeftTablet="xxxs"
                  marginLeftDesktop="xxxs"
                  marginBottom="xxs"
                  marginBottomTablet="none"
                  marginBottomDesktop="none"
                >
                  <Button primary onClick={() => this.presenter.onContract()}>
                    {t('buttons.contract')}
                  </Button>
                </Block>
              )}
              <Block
                grow={1}
                marginRightTablet="xxs"
                marginRightDesktop="xxs"
                marginBottom="xxs"
                marginBottomTablet="none"
                marginBottomDesktop="none"
              >
                <Button grey onClick={this.presenter.onBackToPortal}>
                  {t('buttons.backPortal')}
                </Button>
              </Block>
              {isPolicyContracted && (
                <Block grow={1} flex alignCenter>
                  <div
                    style={{ cursor: 'pointer', display: 'inline-flex' }}
                    onClick={() => this.presenter.onDownloadOfferPDF()}
                  >
                    <ClickableIcon
                      src={PdfSVG}
                      onClick={this.presenter.onDownloadOfferPDF}
                    />
                    <Text as="span" color="primary">
                      {t('buttons.download')}
                    </Text>
                  </div>
                </Block>
              )}
            </Block>
          </>
        ) : (
          <Row marginTop="xxs">
            <Col verticalGutter mobile={12} tablet={12} desktop={12}>
              <Text
                as="div"
                size="sBold"
                color="black"
                marginBottom="xxs"
                uppercase
              >
                {t('summary.subtitle.generalTerms')}
              </Text>
            </Col>

            <Formik
              initialValues={{ informedTermsAndConditions }}
              onSubmit={values => this.handleContractClick(values)}
              validate={values =>
                validateFieldForm(values.informedTermsAndConditions)
              }
              render={({ setFieldValue, values }) => (
                <Form>
                  <Col verticalGutter mobile={12} tablet={12} desktop={12}>
                    <Field
                      component={CheckboxField}
                      name="informedTermsAndConditions"
                      value={values.informedTermsAndConditions}
                      defaultChecked={values.informedTermsAndConditions}
                      label={
                        <Text as="div" size="s" color="black">
                          {t('summary.generalTerms.main')}
                          <span>
                            <LinkText
                              onClick={this.presenter.onShowGeneralConditionPDF}
                              text={t('summary.generalTerms.link')}
                              color="primary"
                            />
                          </span>
                        </Text>
                      }
                      onSelection={() => {
                        setFieldValue(
                          'informedTermsAndConditions',
                          !values.informedTermsAndConditions,
                        )

                        this.setState({
                          informedTermsAndConditions: !this.state
                            .informedTermsAndConditions,
                        })
                      }}
                    />
                  </Col>

                  <Block
                    flex
                    responsive
                    marginLeftTablet="xxxs"
                    marginLeftDesktop="xxxs"
                  >
                    {!isPolicyContracted && (
                      <Block
                        grow={1}
                        marginRightTablet="xxs"
                        marginRightDesktop="xxs"
                        marginBottom="xxs"
                        marginBottomTablet="none"
                        marginBottomDesktop="none"
                      >
                        <Button
                          primary
                          type="submit"
                          disabled={
                            !Boolean(this.state.informedTermsAndConditions)
                          }
                        >
                          {t('buttons.contract')}
                        </Button>
                      </Block>
                    )}

                    <Block
                      grow={1}
                      marginRightTablet="xxs"
                      marginRightDesktop="xxs"
                      marginBottom="xxs"
                      marginBottomTablet="none"
                      marginBottomDesktop="none"
                    >
                      <Button grey onClick={this.presenter.onBack}>
                        {t('buttons.back')}
                      </Button>
                    </Block>
                  </Block>
                </Form>
              )}
            />
          </Row>
        )}

        <Row verticalGutter marginTop="s">
          <Col>
            <ContactBox isCalledFrom="/contract/policy-summary" />
          </Col>
        </Row>
      </PageWrapper>
    )
  }
}

export const PolicyView = withNamespaces()(withRouter(PolicyRaw))
